import './bidsOverviewDataGrid.css'

import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { Box, Stack, Tooltip } from '@mui/material'
import type { GridColDef, GridFilterModel, GridPaginationModel, GridRowParams } from '@mui/x-data-grid'
import type { GridRenderCellParams } from '@mui/x-data-grid-pro'
import type { TFunction } from 'i18next'
import type { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import type { ActivationGroup } from '@/features/activationGroup/types'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import BidsMenu from '@/features/bidding/components/overview/bidsMenu/BidsMenu'
import BidsOverviewFilterToolbar from '@/features/bidding/components/overview/BidsOverviewFilterToolbar'
import CapacityBidStatusChips from '@/features/bidding/components/overview/CapacityBidStatusChips'
import EditBid from '@/features/bidding/components/overview/EditBid'
import EnergyBidStatusChips from '@/features/bidding/components/overview/EnergyBidStatusChips'
import type { MarketProgram } from '@/features/bidding/constants'
import { AFRR_MARKET_PROGRAMS, BidType, MFRR_MARKET_PROGRAMS, Status } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { GET_BIDS_API_STALE_TIME } from '@/features/bidding/endpoints/bids'
import { useBidOverviewSummaryPageQuery } from '@/features/bidding/hooks/useBidOverviewSummaryPageQuery'
import type { Portfolio } from '@/features/bidding/types/bid'
import type { BidOverviewSummaryGroup, BidOverviewSummaryRowItem } from '@/features/bidding/types/bidOverviewSummary'
import { isManuallyAcceptable, isManuallyEditable } from '@/features/bidding/utils/bidStatus'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import { getByCountry } from '@/features/bidding/utils/getBidConfig'
import { buildBidDetailLink, buildBidViewLink } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import { buildBidOverviewSummaryRowItem } from '@/features/bidding/utils/model/buildBidOverviewSummaryRowItem'

type BidsOverviewSummaryDataGridProps = {
  portfolios: Portfolio[] | null
  activationGroups: ActivationGroup[] | null
  selectedCountry: CountryIdentifier
  filterModel: GridFilterModel
  paginationModel: GridPaginationModel
  setPaginationModel: (paginationModel: GridPaginationModel) => void
  setFilterModel: (filterModel: GridFilterModel) => void
}

const BidsOverviewSummaryDataGrid = ({
  portfolios,
  activationGroups,
  selectedCountry,
  paginationModel,
  setPaginationModel,
  filterModel,
  setFilterModel,
}: Readonly<BidsOverviewSummaryDataGridProps>) => {
  const { t } = useTranslation()
  const { bidOverviewSummaryPage, isLoading } = useBidOverviewSummaryPageQuery(
    { country: selectedCountry, filter: filterModel, pagination: paginationModel },
    { staleTime: GET_BIDS_API_STALE_TIME },
  )

  const totalCount = bidOverviewSummaryPage?.totalCount
  const groupedBidOverviews = bidOverviewSummaryPage?.groupedBids

  const hasBids = useMemo(() => {
    if (!groupedBidOverviews || groupedBidOverviews.length === 0 || isLoading) {
      return { energy: false, capacity: false }
    }
    return {
      energy: groupedBidOverviews.some((group: BidOverviewSummaryGroup) => Object.keys(group.energyBids).length > 0),
      capacity: groupedBidOverviews.some(
        (group: BidOverviewSummaryGroup) => Object.keys(group.capacityBids).length > 0,
      ),
    }
  }, [groupedBidOverviews, isLoading])

  const hasEnergyBids = hasBids.energy
  const hasCapacityBids = hasBids.capacity

  return (
    <CustomDataGrid
      disableColumnSorting
      disableDefaultHeaderBackground
      autosizeOptions={{
        columns: [
          'deliveryDay',
          'createdAt',
          'portfolio',
          'marketProgram',
          ...(hasCapacityBids ? ['capacityBidStatus'] : []),
          ...(hasEnergyBids ? ['energyBidStatus'] : []),
        ],
        includeOutliers: true,
        includeHeaders: false,
        expand: true,
      }}
      clickableRows={{
        navigateTo: (params: GridRowParams<BidOverviewSummaryRowItem>) => {
          const row = params.row
          const base = {
            deliveryDay: row.deliveryDay,
            portfolioCode: row.portfolio.code,
            marketProgram: row.marketProgram,
          }

          if (MFRR_MARKET_PROGRAMS.includes(row.marketProgram) || AFRR_MARKET_PROGRAMS.includes(row.marketProgram)) {
            return buildBidDetailLink({ ...base, bidType: rowHasEnergyBid(row) ? BidType.ENERGY : BidType.CAPACITY })
          }

          return buildBidViewLink({ ...base, bidType: getBidTypeParam(row) })
        },
      }}
      columnGroupingModel={getColumnGrouping(t)}
      columns={getColumns(t, hasBids)}
      data-testid="bid-overview-summary-grid"
      filterMode="server"
      filterModel={filterModel}
      getRowId={(row: BidOverviewSummaryRowItem) =>
        row.deliveryDay.toISODate() + row.portfolio.code + row.marketProgram
      }
      isLoading={isLoading}
      paginationMode="server"
      paginationModel={paginationModel}
      rowCount={totalCount ?? 0}
      rows={groupedBidOverviews && activationGroups ? buildRowItems(groupedBidOverviews, activationGroups) : []}
      slotProps={{
        toolbar: {
          portfolios,
        },
      }}
      slots={{ toolbar: BidsOverviewFilterToolbar }}
      onFilterModelChange={setFilterModel}
      onPaginationModelChange={setPaginationModel}
    />
  )
}

const getBidTypeParam = (row: BidOverviewSummaryRowItem): BidType => {
  const capacityBidStatuses = row.capacityBid.uniqueStatuses
  const hasCapacityBid = !(capacityBidStatuses.length === 1 && capacityBidStatuses[0] === Status.NO_BID)

  const energyBidStatuses = row.energyBid.uniqueStatuses
  const hasEnergyBid = !(energyBidStatuses.length === 1 && energyBidStatuses[0] === Status.NO_BID)

  if (hasCapacityBid) {
    return BidType.CAPACITY
  }

  if (hasEnergyBid) {
    return BidType.ENERGY
  }

  return BidType.CAPACITY
}

const rowHasEnergyBid = (row: BidOverviewSummaryRowItem): boolean =>
  !(row.energyBid.uniqueStatuses.length === 1 && row.energyBid.uniqueStatuses[0] === Status.NO_BID)

const buildRowItems = (groupedBids: BidOverviewSummaryGroup[], activationGroups: ActivationGroup[]) =>
  groupedBids.map((group) => buildBidOverviewSummaryRowItem(group, activationGroups)).filter((rowItem) => !!rowItem)

const getColumns = (
  t: TFunction,
  hasBids: { energy: boolean; capacity: boolean },
): GridColDef<BidOverviewSummaryRowItem>[] => [
  {
    field: 'deliveryDay',
    headerName: t('bidding.overview.header.delivery_day'),
    valueFormatter: (value: MarketDate) => value.getStartOfDay().toFormat('dd LLL, yyyy'),
    flex: 2,
    headerClassName: 'default-header',
  },
  {
    field: 'createdAt',
    headerName: t('bidding.overview.header.created_at'),
    valueFormatter: (value: DateTime | undefined) => value?.toFormat('dd LLL, yyyy') ?? '-',
    flex: 2,
    headerClassName: 'default-header',
  },
  {
    field: 'portfolio',
    headerName: t('bidding.overview.header.portfolio'),
    valueFormatter: (value: Portfolio) => value.name,
    flex: 3,
    headerClassName: 'default-header',
  },
  {
    field: 'marketProgram',
    headerName: t('bidding.overview.header.market_program'),
    flex: 3,
    valueFormatter: (value: BidOverviewSummaryRowItem['marketProgram']) => t(`bidding.market_program.${value}`),
    headerClassName: 'default-header',
  },
  {
    field: 'activationGroupCodes',
    type: 'singleSelect',
    headerName: t('bidding.overview.header.activation_groups'),
    flex: 0,
    width: 50,
    renderCell: (params: GridRenderCellParams<BidOverviewSummaryRowItem>) => params.row.activationGroupCodes.length,
    headerClassName: 'default-header',
  },
  ...(hasBids.capacity ? getCapacityBidColumns(t) : []),
  ...(hasBids.energy ? getEnergyBidColumns(t) : []),
]

const getCapacityBidColumns = (t: TFunction) => [
  {
    field: 'capacityBidStatus',
    headerName: t('bidding.overview.header.status'),
    flex: 2,
    minWidth: 160,
    valueGetter: (_, params: BidOverviewSummaryRowItem) => params.capacityBid.uniqueStatuses,
    renderHeader: () => (
      <StatusColumnHeader
        hint={t('bidding.overview.header.capacity_status_hint')}
        label={t('bidding.overview.header.status')}
      />
    ),
    renderCell: (params: GridRenderCellParams<BidOverviewSummaryRowItem>) => (
      <CapacityBidStatusChips cell={params.row.capacityBid} />
    ),
    cellClassName: 'capacity-bid-column',
    headerClassName: 'capacity-bid-column',
  },
  {
    field: 'capacityBidActions',
    headerName: '',
    disableColumnMenu: true,
    flex: 0,
    minWidth: 100,
    renderCell: (params: GridRenderCellParams<BidOverviewSummaryRowItem>) => (
      <GridActionsColumn bidType={BidType.CAPACITY} params={params} />
    ),
    cellClassName: 'capacity-bid-actions-column',
    headerClassName: 'capacity-bid-actions-column',
  },
]

const getEnergyBidColumns = (t: TFunction) => [
  {
    field: 'energyBidStatus',
    headerName: t('bidding.overview.header.status'),
    minWidth: 140,
    valueGetter: (_, params: BidOverviewSummaryRowItem) => params.energyBid.uniqueStatuses,
    renderHeader: () => (
      <StatusColumnHeader
        hint={t('bidding.overview.header.energy_status_hint')}
        label={t('bidding.overview.header.status')}
      />
    ),
    renderCell: (params: GridRenderCellParams<BidOverviewSummaryRowItem>) => (
      <EnergyBidStatusChips statusCounts={params.row.energyBid.statusCounts} />
    ),
    cellClassName: 'energy-bid-column',
    headerClassName: 'energy-bid-column',
  },
  {
    field: 'energyBidActions',
    headerName: '',
    disableColumnMenu: true,
    flex: 0,
    minWidth: 100,
    renderCell: (params: GridRenderCellParams<BidOverviewSummaryRowItem>) => (
      <GridActionsColumn bidType={BidType.ENERGY} params={params} />
    ),
    cellClassName: 'energy-bid-column',
    headerClassName: 'energy-bid-column',
  },
]

const getColumnGrouping = (t: TFunction) => [
  {
    groupId: t('bidding.overview.header.capacity'),
    children: [{ field: 'capacityBidStatus' }, { field: 'capacityBidActions' }],
    headerClassName: 'capacity-bid-actions-column',
  },
  {
    groupId: t('bidding.overview.header.energy'),
    children: [{ field: 'energyBidStatus' }, { field: 'energyBidActions' }],
    headerClassName: 'energy-bid-column',
  },
]

const StatusColumnHeader = ({ label, hint }: { label: string; hint: string }) => (
  <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{hint}</Box>}>
    <Stack alignItems="center" direction="row" gap={0.5}>
      <span>{label}</span>
      <InfoOutlined sx={{ fontSize: 16, opacity: 0.6 }} />
    </Stack>
  </Tooltip>
)

const GridActionsColumn = ({
  params,
  bidType,
}: {
  params: GridRenderCellParams<BidOverviewSummaryRowItem>
  bidType: BidType
}) => {
  const { selectedCountry } = useBiddingContext()
  const { permissions } = usePermissions()
  const canMutateBidding = permissions.has('biddingMutation')
  const bidUniqueStatuses =
    bidType === BidType.CAPACITY ? params.row.capacityBid.uniqueStatuses : params.row.energyBid.uniqueStatuses
  const noBid = isNoBid(bidUniqueStatuses)

  return (
    <Stack flexDirection="row" justifyContent="space-between" width="100%">
      {!noBid && canMutateBidding && (
        <>
          <Box>
            <EditBid
              groupedBidParams={{
                deliveryDay: params.row.deliveryDay,
                portfolioCode: params.row.portfolio.code,
                marketProgram: params.row.marketProgram,
                bidType: bidType,
              }}
            />
          </Box>
          <Box>
            {isMenuVisible(bidUniqueStatuses, params.row.marketProgram, selectedCountry) && (
              <BidsMenu
                groupedBidParams={{
                  deliveryDay: params.row.deliveryDay,
                  portfolioCode: params.row.portfolio.code,
                  marketProgram: params.row.marketProgram,
                  bidType: bidType,
                }}
                statuses={bidUniqueStatuses}
              />
            )}
          </Box>
        </>
      )}
    </Stack>
  )
}

const isNoBid = (bidUniqueStatuses: Status[]) => {
  return bidUniqueStatuses.length === 1 && bidUniqueStatuses.includes(Status.NO_BID)
}

const isMenuVisible = (uniqueStatuses: Status[], marketProgram: MarketProgram, selectedCountry: CountryIdentifier) =>
  uniqueStatuses.some((status) => isManuallyAcceptable(status) || isManuallyEditable(status)) &&
  isEditableMarketProgram(marketProgram, selectedCountry)

function isEditableMarketProgram(marketProgram: MarketProgram, selectedCountry: CountryIdentifier): boolean {
  const { marketsThatCanBeManuallyEdited } = getByCountry(selectedCountry)

  return marketsThatCanBeManuallyEdited.includes(marketProgram)
}

export default BidsOverviewSummaryDataGrid
